import { Data, InteractiveBlock, DialogContent } from "api/dialog";
import ContentBlocks from "./components/Content";
import { animated, config, useTransition } from "@react-spring/web";
import { useTransitionHeight } from "utils/useTransitionHeight";
import { Row, Col } from "antd";
import {
  Context,
  PersistentDialogHistoryItem,
  useBitrixChat,
} from "components/Scenario/utils";
import { usePrevious } from "react-use";
import _styles from "./Dialog.module.scss";
import classNames from "classnames/bind";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMobileQuery } from "utils/utils";
const styles = classNames.bind(_styles);

export type ChangeValue<V> = {
  path?: string;
  value?: V;
  next?: string;
  resetProduct?: boolean;

  name?: string;
  block?: InteractiveBlock;

  clearCallStack?: boolean;
  progressScenario?: boolean;
  pushStack?: boolean;
  rerenderKeyboard?: boolean;
};

export type DialogHistoryItem = (
  | { dialogBlockType: "bot" | "user" }
  | { dialogBlockType: "operator"; avatar?: string }
) &
  DialogContent;

export interface DialogProps {
  html?: boolean;
  dialog: PersistentDialogHistoryItem[];
  onChange?: () => void;
}
export type NullableChangeValue<V> = [V] extends [null]
  ? ChangeValue<V> | null
  : ChangeValue<V>;
export interface DialogBlockProps<V = unknown> {
  mobile?: boolean;
  className?: string;
  context: Context;
  data: Data;
  contrastGrayscale?: number;
  bitrix: ReturnType<typeof useBitrixChat>;
  onChange?: (
    ...args: [value?: NullableChangeValue<V>, ...display: DialogHistoryItem[]]
  ) => void;
  onBack?: () => void;
}

const BASE_TRAIL_TIME = 500;

export const DialogBox = ({ html, dialog, onChange }: DialogProps) => {
  const previousLength = usePrevious(dialog.length);
  const isMobile = useMobileQuery();
  const [animating, setAnimating] = useState(0);
  const prevAnimating = usePrevious(animating);
  const deltaAnimating = animating - (prevAnimating ?? 0);
  const listTransitions = useTransition(dialog, {
    keys(item) {
      return item.id;
    },
    config: { ...config.default },
    // reduce time so that animation isnt too long
    trail: previousLength
      ? dialog.length - previousLength <= 0
        ? 0 // remove without trails
        : BASE_TRAIL_TIME / (dialog.length - previousLength)
      : BASE_TRAIL_TIME / Math.max(1, dialog.length),
    from: { opacity: 0, scale: 0 },
    enter: { opacity: 1, scale: 1 },
    leave: { opacity: 0, scale: 0 },
    // exitBeforeEnter: true, // when used may stop updating on list change
    onStart() {
      setAnimating((animating) => animating + 1);
    },
    onRest() {
      setAnimating((animating) => animating - 1);
    },
  });
  const listRef = useRef<HTMLDivElement>();
  const [ref, setRef] = useState<Element | null>(null);
  const _onChange = useCallback(() => {
    if (!ref) return onChange?.();
    ref.scrollIntoView({
      behavior: "instant",
      block: "start",
      inline: "nearest",
    });
  }, [onChange, ref]);
  const [style, setListHeightRef] = useTransitionHeight<HTMLDivElement>({
    config: { ...config.default },
    onChange: _onChange,
  });

  useEffect(() => {
    if (!isMobile) return;
    if (!listRef.current) return;
    if (animating !== 0 && deltaAnimating > 0) {
      const msgs = listRef.current.querySelectorAll(`.${styles("user")}`);
      setRef(msgs[msgs.length - 1] ?? null);
    }
    if (animating === 0 && deltaAnimating < 0) {
      setRef(null);
    }
  }, [dialog, animating, deltaAnimating, isMobile]);

  return (
    <animated.div style={style}>
      <div
        ref={(ref) => {
          if (!ref) return;
          listRef.current = ref;
          setListHeightRef(ref);
        }}
        className={styles("blocksList")}
      >
        {listTransitions((style, item) => {
          const { dialogBlockType, ...block } = item;
          if (block.type === "text") block.html = block.html ?? html;
          if (block.type === "text") block.checked = dialogBlockType === "user";
          const Component = ContentBlocks(block);
          return (
            <animated.div style={style} className={styles(dialogBlockType)}>
              <Row gutter={[12, 0]} wrap={false}>
                {item.dialogBlockType === "operator" && item.avatar && (
                  <Col>
                    <img
                      className={styles("avatar")}
                      src={item.avatar}
                      alt="operator-avatar"
                    />
                  </Col>
                )}
                <Col flex={1} className={styles("inner")}>
                  <div className={styles(dialogBlockType, block.type, "block")}>
                    <Component />
                  </div>
                </Col>
              </Row>
            </animated.div>
          );
        })}
      </div>
    </animated.div>
  );
};
